// ExtraAmountReporting.js
import React, { useEffect, useState } from "react";
import { Container, Table, Spinner, Alert, Input, Button } from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ExtraAmountReporting = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [paymentId, setPaymentId] = useState("");
  const [tranId, setTranId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [filtering, setFiltering] = useState(false);
  const [filterType, setFilterType] = useState("");

  // Fetch all data on component mount
  useEffect(() => {
    fetchAllExtraAmounts();
  }, []);

  // Fetch all extra amount records
  const fetchAllExtraAmounts = () => {
    setLoading(true);
    setError("");
    axiosInstance
      .get(URL.REPORTING_PAYMENT_SUMMARY)
      .then((res) => {
        const filteredData = (res.data || []).filter(
          (row) => row.total_extra_amount > 0
        );
        setData(sortByDate(filteredData));
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch extra amount reporting data.");
        setLoading(false);
      });
  };

  // Sort records by transaction date (latest first)
  const sortByDate = (records) => {
    return records.sort((a, b) => new Date(b.tran_date) - new Date(a.tran_date));
  };

  // Search handler
  const handleSearch = () => {
    setError("");
    if (!fromDate && !toDate && !paymentId && !tranId && !customerId) {
      fetchAllExtraAmounts();
      return;
    }

    setFiltering(true);
    const params = {};
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    if (paymentId) params.paymentId = paymentId;
    if (tranId) params.tranId = tranId;
    if (customerId) params.cid = customerId;

    axiosInstance
      .get(URL.REPORTING_PAYMENT_SUMMARY, { params })
      .then((res) => {
        const filteredData = (res.data || []).filter(
          (row) => row.total_extra_amount > 0
        );
        setData(sortByDate(filteredData));
        setFiltering(false);
      })
      .catch(() => {
        setError("Failed to fetch data with filters.");
        setFiltering(false);
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  // Export filtered data to Excel
  const exportToExcel = () => {
    if (!data || data.length === 0) {
      alert("No data to export!");
      return;
    }
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Extra Amount Report");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "Extra_Amount_Report.xlsx");
  };

  if (loading) return <Spinner />;
  if (error) return <Alert color="danger">{error}</Alert>;

  return (
    <Container fluid>
      <h2>Extra Amount Reporting</h2>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "15px",
          marginBottom: "20px",
          alignItems: "flex-end",
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Filter Type Dropdown */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={{ fontSize: "12px", fontWeight: "500" }}>Filter Type</label>
          <Input
            type="select"
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setPaymentId("");
              setCustomerId("");
              setTranId("");
            }}
            style={{ fontSize: "12px", fontWeight: "500" }}
          >
            <option value="">Select Filter</option>
            <option value="paymentId">Payment ID</option>
            <option value="customerId">Customer ID</option>
            <option value="tranId">Tran ID</option>
          </Input>
        </div>

        {/* Conditional Filter Input */}
        {filterType && (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: "12px", fontWeight: "500" }}>
              {filterType === "paymentId"
                ? "Payment ID"
                : filterType === "customerId"
                ? "Customer ID"
                : "Tran ID"}
            </label>
            <Input
              type={filterType === "customerId" ? "number" : "text"}
              placeholder={
                filterType === "paymentId"
                  ? "Enter Payment ID"
                  : filterType === "customerId"
                  ? "Enter Customer ID"
                  : "Enter Tran ID"
              }
              value={
                filterType === "paymentId"
                  ? paymentId
                  : filterType === "customerId"
                  ? customerId
                  : tranId
              }
              onChange={(e) => {
                if (filterType === "paymentId") setPaymentId(e.target.value);
                else if (filterType === "customerId") setCustomerId(e.target.value);
                else setTranId(e.target.value);
              }}
              style={{ fontSize: "12px", fontWeight: "500" }}
            />
          </div>
        )}

        {/* From Date */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={{ fontSize: "12px", fontWeight: "500" }}>From Date</label>
          <Input
            type="date"
            max={new Date().toISOString().split("T")[0]}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ fontSize: "12px", fontWeight: "500" }}
          />
        </div>

        {/* To Date */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={{ fontSize: "12px", fontWeight: "500" }}>To Date</label>
          <Input
            type="date"
            max={new Date().toISOString().split("T")[0]}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ fontSize: "12px", fontWeight: "500" }}
          />
        </div>

        {/* Search Button */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label>&nbsp;</label>
          <Button
            color="primary"
            onClick={handleSearch}
            disabled={filtering}
            style={{ fontSize: "12px", fontWeight: "500" }}
          >
            {filtering ? "Loading..." : "Search"}
          </Button>
        </div>

        {/* Export Button */}
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label>&nbsp;</label>
          <Button
            color="success"
            onClick={exportToExcel}
            style={{ fontSize: "12px", fontWeight: "500" }}
          >
            Export to Excel
          </Button>
        </div>
      </div>

      {/* Table */}
      <Table bordered striped responsive hover>
        <thead>
          <tr>
            <th>S.No.</th>
            <th>Payment ID</th>
            <th>Customer ID</th>
            <th>Tran Date</th>
            <th>Tran ID</th>
            <th>Total Payment</th>
            <th>Extra Amount</th>
            <th>Settled Amount</th>
            <th>Principal Settled</th>
            <th>Interest Settled</th>
            <th>Fees Settled</th>
          </tr>
        </thead>
        <tbody>
          {data.length > 0 && !data[0]?.message ? (
            data.map((row, i) => (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>{row.payment_id}</td>
                <td>{row.customer_id}</td>
                <td>{row.tran_date}</td>
                <td>{row.tran_id}</td>
                <td>₹{row.total_payment_amount?.toFixed(2) || "0.00"}</td>
                <td className="text-success">₹{row.total_extra_amount?.toFixed(2) || "0.00"}</td>
                <td>₹{row.total_settled_amount?.toFixed(2) || "0.00"}</td>
                <td>₹{row.total_principal_settled?.toFixed(2) || "0.00"}</td>
                <td>₹{row.total_interest_settled?.toFixed(2) || "0.00"}</td>
                <td>₹{row.total_fees_settled?.toFixed(2) || "0.00"}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="11" style={{ textAlign: "center" }}>
                {data[0]?.message || "No data found."}
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Container>
  );
};

export default ExtraAmountReporting;
